import { useState, useMemo } from "react";

import { Box, Typography } from "@mui/material";

import { FolderOpenIcon } from "@phosphor-icons/react/dist/ssr";

import { ExampleModal, TabHeader } from "@/components/Luna/features";
import useLesson from "@/contexts/useLesson";

import { ExampleItem } from "./index";

interface Props {
  isFocused: boolean;
}

const Examples = ({ isFocused }: Props) => {
  const { lessonInteractionProfile } = useLesson();

  const [selectedExampleIndex, setSelectedExampleIndex] = useState<
    number | null
  >(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const examples = useMemo(
    () =>
      lessonInteractionProfile?.realtimeResources?.examples?.filter(
        (example) => example.isApproved
      ) || [],
    [lessonInteractionProfile]
  );
  const sortedExamples = [...examples].reverse();

  const handleOpenModal = (index: number) => {
    setSelectedExampleIndex(index);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedExampleIndex(null);
  };

  return (
    <Box
      width="100%"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TabHeader
        title="Examples"
        icon={<FolderOpenIcon size={16} weight="bold" />}
      />
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0, // Ensures the Box can shrink properly in a flex column
        }}
      >
        {examples?.length < 1 ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              px: 4,
            }}
          >
            <Typography
              variant="text15B"
              color="textSecondary"
              textAlign="center"
              sx={{ width: "100%" }}
            >
              No examples available yet.
            </Typography>
            <Typography
              variant="text13L"
              color="textSecondary"
              textAlign="center"
              sx={{ width: "100%" }}
            >
              Your tutor will add examples here to help you understand the
              concepts better.
            </Typography>
          </Box>
        ) : (
          <Box>
            {sortedExamples.map((example) => {
              const originalIndex = examples.findIndex(
                (e) => e.id === example.id
              );
              return (
                <ExampleItem
                  key={example.id}
                  example={example}
                  index={originalIndex}
                  onOpenModal={handleOpenModal}
                  isFocused={isFocused}
                />
              );
            })}
          </Box>
        )}
      </Box>
      {selectedExampleIndex !== null && (
        <ExampleModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          example={
            selectedExampleIndex !== null
              ? examples[selectedExampleIndex]
              : null
          }
          index={selectedExampleIndex}
        />
      )}
    </Box>
  );
};

export default Examples;
